import { useState, useCallback, useMemo } from 'react';
import { uazapiService, disconnectInstance } from '../../../../services/uazapiService';
import { v4 as uuidv4 } from 'uuid';
import {
    createIntegrationPreRegister,
    deleteIntegrationByInstance
} from './integrationRepository';
import { useConnectionPolling } from './useConnectionPolling';

// ============================================================================
// TYPES
// ============================================================================

export type ConnectionStep = 'list' | 'create' | 'qrcode';

// ============================================================================
// UTILS
// ============================================================================

/**
 * Gera ID amigável para instância baseado no nome.
 */
function generateInstanceId(name: string): string {
    const cleanName = name.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-');
    return `${cleanName}-${uuidv4().slice(0, 4)}`;
}

// ============================================================================
// HOOK
// ============================================================================

export const useWhatsAppConnection = (onConnectionSuccess?: () => void) => {
    const [step, setStep] = useState<ConnectionStep>('list');
    const [isLoading, setIsLoading] = useState(false);
    const [qrCode, setQrCode] = useState<string | null>(null);
    const [currentInstance, setCurrentInstance] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Callbacks para polling
    const pollingCallbacks = useMemo(() => ({
        onConnected: () => {
            setStep('list');
            if (onConnectionSuccess) onConnectionSuccess();
        },
        onError: (message: string) => {
            setError(message);
        }
    }), [onConnectionSuccess]);

    const {
        cleanup,
        startPollingAfterQRCode,
        startPollingForReconnect
    } = useConnectionPolling(pollingCallbacks);

    /**
     * Reseta o fluxo de conexão.
     */
    const resetFlow = useCallback(() => {
        cleanup();
        setStep('list');
        setQrCode(null);
        setCurrentInstance(null);
        setError(null);
    }, [cleanup]);

    /**
     * Cancela conexão em andamento.
     */
    const cancelConnection = useCallback(async () => {
        cleanup();
        if (currentInstance) {
            try {
                await disconnectInstance(currentInstance);
                await deleteIntegrationByInstance(currentInstance);
            } catch (e) {
                console.error('[Connection] Erro ao cancelar instância:', e);
            }
        }
        resetFlow();
    }, [cleanup, currentInstance, resetFlow]);

    /**
     * Inicia processo de criação de nova conexão.
     */
    const startConnection = async (name: string, phoneNumber: string) => {
        setIsLoading(true);
        setError(null);

        const instanceId = generateInstanceId(name);

        try {
            // 1. Pré-registro no banco
            const integrationId = await createIntegrationPreRegister(instanceId, name);
            if (!integrationId) throw new Error('Falha ao registrar integração no banco');

            setCurrentInstance(instanceId);

            // 2. Cria instância na UazAPI (com retry)
            let createResponse;
            try {
                createResponse = await uazapiService.createInstance(instanceId, phoneNumber);
            } catch (createErr: any) {
                // Se falhou por overload, tenta mais uma vez após 3s
                if (createErr.message?.includes('sobrecarregado') || createErr.message?.includes('Edge Function')) {
                    console.log('[Connection] Edge Function overload, retrying in 3s...');
                    await new Promise(r => setTimeout(r, 3000));
                    createResponse = await uazapiService.createInstance(instanceId, phoneNumber);
                } else {
                    throw createErr;
                }
            }
            console.log('[Connection] Resposta Create:', JSON.stringify(createResponse));

            // Validação de erros
            if (createResponse?.error || (createResponse?.status && createResponse.status >= 400)) {
                throw new Error(createResponse.message || createResponse.error || 'Erro ao criar instância');
            }

            // 3. Obtém QR Code
            let qrCodeBase64 = createResponse?.instance?.qrcode
                || createResponse?.qrcode?.base64
                || createResponse?.base64
                || createResponse?.qrcode;

            if (!qrCodeBase64) {
                console.log('[Connection] QR Code não veio na criação, iniciando polling...');
                qrCodeBase64 = await pollForQRCode(instanceId, integrationId, name);
            }

            if (qrCodeBase64) {
                setQrCode(qrCodeBase64);
                setStep('qrcode');
                startPollingAfterQRCode(instanceId, name, integrationId);
            } else {
                console.warn('[Connection] QR Code não obtido.');
                setError('O QR Code demorou para carregar. Tente novamente.');
                setStep('qrcode');
            }

        } catch (err: any) {
            setError(err.message || 'Erro ao criar conexão');
            disconnectInstance(instanceId).catch(() => { });
            deleteIntegrationByInstance(instanceId);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Polling para obter QR Code quando não vem na criação.
     */
    async function pollForQRCode(
        instanceId: string,
        integrationId: string,
        name: string
    ): Promise<string | null> {
        let attempts = 0;

        while (attempts < 5) {
            console.log(`[Connection] Tentativa ${attempts + 1} de buscar QR Code...`);
            await new Promise(resolve => setTimeout(resolve, 3000));

            try {
                // Verifica se já conectou
                const statusState = await uazapiService.getConnectionState(instanceId);
                if (statusState?.instance?.state === 'open') {
                    console.log('[Connection] 🚀 Instância já conectada!');

                    const profileData = await uazapiService.getInstanceProfile(instanceId);
                    await import('./integrationRepository').then(repo =>
                        repo.updateIntegrationAfterConnect(integrationId, instanceId, profileData || { name }, name)
                    );

                    try { await uazapiService.setupWebhook(instanceId); } catch (e) { console.warn(e); }

                    setStep('list');
                    if (onConnectionSuccess) onConnectionSuccess();
                    return null;
                }

                // Tenta pegar QR Code
                const qrData = await uazapiService.getQRCode(instanceId);
                if (qrData?.base64 || qrData?.code) {
                    return qrData.base64 || qrData.code;
                }
            } catch (loopErr) {
                console.warn(`[Connection] Erro na tentativa ${attempts + 1}:`, loopErr);
            }

            attempts++;
        }

        return null;
    }

    return {
        // Estado
        step,
        setStep,
        isLoading,
        qrCode,
        setQrCode,
        error,

        // Ações
        startConnection,
        cancelConnection,
        resetFlow,
        startStatusPollingForReconnect: startPollingForReconnect
    };
};
